import { useState } from "react";
import type { ChangeEvent } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { createBackgroundJobSchedule } from "@/api/background-job/createBackgroundJobSchedule";
import { deleteBackgroundJobSchedule } from "@/api/background-job/deleteBackgroundJobSchedule";
import { getBackgroundJobKindList } from "@/api/background-job/getBackgroundJobKindList";
import { getBackgroundJobScheduleList } from "@/api/background-job/getBackgroundJobScheduleList";
import { updateBackgroundJobSchedule } from "@/api/background-job/updateBackgroundJobSchedule";
import type { BackgroundJobSchedule, BackgroundJobScheduleInput } from "@/api/background-job/_models/BackgroundJobSchedule";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { TimeAgo } from "@/components/TimeAgo";
import { toErrorText } from "@/components/snapshot/snapshot.utils";
import { JobStatusChip } from "@/features/job/JobStatusChip";
import { toJobKindLabel, toJobScheduleDescription, toJobSummary } from "@/features/job/job.utils";
import { JobScheduleDialog } from "./JobScheduleDialog";
import { toJobScheduleEmptyInput } from "./jobSchedule.utils";

type Editing = BackgroundJobSchedule | null | undefined;

const toScheduleInput = (schedule: BackgroundJobSchedule): BackgroundJobScheduleInput => {
  return {
    name: schedule.name,
    kind: schedule.kind,
    frequency: schedule.frequency,
    intervalMinutes: schedule.intervalMinutes,
    minute: schedule.minute,
    time: schedule.time,
    weekdays: schedule.weekdays,
    enabled: schedule.enabled,
  };
};

/**
 * The Schedules page: the schedules that start background jobs on their own — when each runs
 * (Sydney time), its next and last run, on / off, edit and delete. Saving one also writes its
 * EventBridge schedule, which is what fires it.
 */
export const JobSchedule = () => {
  const [editing, setEditing] = useState<Editing>(undefined);
  const [deleting, setDeleting] = useState<BackgroundJobSchedule | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const schedulesQuery = getBackgroundJobScheduleList.useQuery(undefined, { pollingInterval: 60_000 });
  const kindsQuery = getBackgroundJobKindList.useQuery();
  const [createSchedule, createState] = createBackgroundJobSchedule.useMutation();
  const [updateSchedule, updateState] = updateBackgroundJobSchedule.useMutation();
  const [deleteSchedule, deleteState] = deleteBackgroundJobSchedule.useMutation();
  const schedules = schedulesQuery.data ?? [];
  const kinds = kindsQuery.data ?? [];
  const firstSchedulableKind = kinds.find((kind) => kind.schedulable)?.kind ?? "";
  const isSaving = createState.isLoading || updateState.isLoading;

  const handleAdd = () => {
    setSaveError(null);
    setEditing(null);
  };

  const handleEdit = (schedule: BackgroundJobSchedule) => {
    setSaveError(null);
    setEditing(schedule);
  };

  const handleCancel = () => {
    setEditing(undefined);
  };

  const handleSave = async (input: BackgroundJobScheduleInput) => {
    setSaveError(null);
    try {
      if (editing) await updateSchedule({ id: editing.id, schedule: input }).unwrap();
      else await createSchedule(input).unwrap();
      setEditing(undefined);
    }
    catch (e) {
      setSaveError(toErrorText(e));
    }
  };

  const handleToggle = async (schedule: BackgroundJobSchedule, event: ChangeEvent<HTMLInputElement>) => {
    setToggleError(null);
    const input = { ...toScheduleInput(schedule), enabled: event.target.checked };
    try {
      await updateSchedule({ id: schedule.id, schedule: input }).unwrap();
    }
    catch (e) {
      setToggleError(`${schedule.name}: ${toErrorText(e)}`);
    }
  };

  const handleDeleteAsk = (schedule: BackgroundJobSchedule) => {
    setDeleting(schedule);
  };

  const handleDeleteCancel = () => {
    setDeleting(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleting) return;
    try {
      await deleteSchedule(deleting.id).unwrap();
      setDeleting(null);
    }
    catch (e) {
      setToggleError(`${deleting.name}: ${toErrorText(e)}`);
      setDeleting(null);
    }
  };

  let initial = toJobScheduleEmptyInput(firstSchedulableKind);
  if (editing) initial = toScheduleInput(editing);

  return (
    <Page>
      <PageHeader title="Schedules">
        <Button
          disabled={!firstSchedulableKind}
          onClick={handleAdd}
          size="small"
          startIcon={<AddIcon />}
          variant="contained"
        >
          Add schedule
        </Button>
      </PageHeader>
      <Box sx={{ px: 3 }}>
        {schedulesQuery.isError && <Alert severity="error">Failed to load schedules: {toErrorText(schedulesQuery.error)}</Alert>}
        {toggleError && (
          <Alert
            onClose={() => setToggleError(null)}
            severity="error"
            sx={{ mb: 2 }}
          >
            {toggleError}
          </Alert>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Job</TableCell>
              <TableCell>Runs (Sydney)</TableCell>
              <TableCell>Next run</TableCell>
              <TableCell>Last run</TableCell>
              <TableCell>On</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules.length === 0 && !schedulesQuery.isLoading && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography
                    color="text.secondary"
                    sx={{ py: 2 }}
                    variant="body2"
                  >
                    No schedules yet — add one to run a job on its own (e.g. the eStore stock pull, hourly).
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {schedules.map((schedule) => (
              <TableRow key={schedule.id}>
                <TableCell sx={{ fontWeight: 500 }}>{schedule.name}</TableCell>
                <TableCell>{toJobKindLabel(kinds, schedule.kind)}</TableCell>
                <TableCell>{toJobScheduleDescription(schedule)}</TableCell>
                <TableCell>
                  {schedule.nextRunAt ? <TimeAgo iso={schedule.nextRunAt} /> : "—"}
                </TableCell>
                <TableCell>
                  {schedule.lastJob ? (
                    <Tooltip title={toJobSummary(schedule.lastJob)}>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: "center" }}
                      >
                        <JobStatusChip status={schedule.lastJob.status} />
                        <TimeAgo iso={schedule.lastJob.startedAt ?? schedule.lastJob.createdAt} />
                      </Stack>
                    </Tooltip>
                  ) : "Never"}
                </TableCell>
                <TableCell>
                  <Switch
                    checked={schedule.enabled}
                    disabled={updateState.isLoading}
                    onChange={(event) => handleToggle(schedule, event)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    aria-label={`Edit ${schedule.name}`}
                    onClick={() => handleEdit(schedule)}
                    size="small"
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label={`Delete ${schedule.name}`}
                    onClick={() => handleDeleteAsk(schedule)}
                    size="small"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {editing !== undefined && (
          <JobScheduleDialog
            error={saveError}
            initial={initial}
            isNew={editing === null}
            isSaving={isSaving}
            key={editing?.id ?? "new"}
            kinds={kinds}
            onCancel={handleCancel}
            onSave={handleSave}
          />
        )}

        <Dialog
          onClose={handleDeleteCancel}
          open={deleting !== null}
        >
          <DialogTitle>Delete schedule?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              &ldquo;{deleting?.name}&rdquo; stops running. Its past runs stay on the Jobs page.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>Cancel</Button>
            <Button
              color="error"
              disabled={deleteState.isLoading}
              onClick={handleDeleteConfirm}
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
};
